import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LogOut, TrendingUp, Users, Mail, Eye, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, sub, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="border border-emerald-900/15 bg-white/40 p-7"
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center justify-between text-emerald-900/50">
      <span className="text-[0.7rem] uppercase tracking-[0.18em]">{label}</span>
      <Icon className="h-4 w-4" />
    </div>
    <p className="mt-5 font-serif text-4xl md:text-5xl text-emerald-900 leading-none">{value}</p>
    {sub && <p className="mt-2 text-sm text-base-text/50">{sub}</p>}
  </motion.div>
);

export default function Analytics() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/analytics`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.status === 401 ? "unauth" : "error"));
  }, []);

  useEffect(() => {
    if (error === "unauth") navigate("/login");
  }, [error, navigate]);

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-base-bg" data-testid="analytics-page">
      {/* Top bar */}
      <header className="border-b border-emerald-900/15 bg-base-bg/90 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif text-2xl text-emerald-900">TILT</span>
            <span className="text-[0.7rem] uppercase tracking-[0.2em] text-emerald-900/50 border-l border-emerald-900/20 pl-4">Admin Panel</span>
          </div>
          <div className="flex items-center gap-6">
            {user?.role === "superadmin" && (
              <button data-testid="analytics-goto-admin" onClick={() => navigate("/admin")} className="hidden sm:inline text-[0.72rem] uppercase tracking-[0.14em] text-emerald-900 hover:opacity-70 transition-opacity">
                Super Admin Panel →
              </button>
            )}
            <span className="hidden sm:block text-sm text-base-text/60">{user?.email}</span>
            <button data-testid="logout-button" onClick={doLogout} className="inline-flex items-center gap-2 text-[0.75rem] uppercase tracking-[0.16em] text-emerald-900 hover:opacity-70 transition-opacity">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-14">
        <div className="mb-12">
          <p className="text-[0.72rem] uppercase tracking-[0.22em] text-emerald-900/50">Overview</p>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl text-emerald-900 leading-none">Website analytics</h1>
        </div>

        {!data && !error && <p className="text-base-text/50">Loading insights…</p>}
        {error === "error" && <p className="text-red-700">Could not load analytics. Please retry.</p>}

        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Stat i={0} icon={Eye} label="Total Visits" value={data.totals.visits} sub="All time" />
              <Stat i={1} icon={Users} label="Unique Visitors" value={data.totals.unique_visitors} sub="By IP" />
              <Stat i={2} icon={Mail} label="Contacts" value={data.totals.contacts} sub="Propositions received" />
              <Stat i={3} icon={TrendingUp} label="Conversion" value={`${data.totals.conversion_rate}%`} sub="Contacts ÷ visits" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="mt-6 border border-emerald-900/15 bg-white/40 p-6 md:p-8"
              data-testid="analytics-chart"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-serif text-2xl text-emerald-900">Last 14 days</h2>
                  <p className="text-sm text-base-text/50 mt-1">Visits and contact submissions</p>
                </div>
                <div className="flex items-center gap-5 text-[0.72rem] uppercase tracking-[0.14em]">
                  <span className="flex items-center gap-2 text-emerald-900"><i className="h-2.5 w-2.5 bg-emerald-900 inline-block" />Visits</span>
                  <span className="flex items-center gap-2 text-emerald-900/50"><i className="h-2.5 w-2.5 inline-block" style={{ background: "#B99A5B" }} />Contacts</span>
                </div>
              </div>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#063A26" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#063A26" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#B99A5B" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#B99A5B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#063A26" strokeOpacity={0.08} vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#1A1A1A80" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#1A1A1A80" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ border: "1px solid #063A2633", borderRadius: 0, fontSize: 13 }} />
                    <Area type="monotone" dataKey="visits" stroke="#063A26" strokeWidth={2} fill="url(#gv)" />
                    <Area type="monotone" dataKey="contacts" stroke="#B99A5B" strokeWidth={2} fill="url(#gc)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="mt-6 border border-emerald-900/15 bg-white/40 p-6 md:p-8"
              data-testid="analytics-referrers"
            >
              <h2 className="font-serif text-2xl text-emerald-900 mb-6">Top sources</h2>
              {data.top_referrers.length === 0 && <p className="text-base-text/50 text-sm">No traffic recorded yet.</p>}
              <div className="divide-y divide-emerald-900/10">
                {data.top_referrers.map((r) => (
                  <div key={r.source} className="flex items-center justify-between py-3.5">
                    <span className="flex items-center gap-2 text-base-text/80"><ArrowUpRight className="h-4 w-4 text-emerald-900/40" />{r.source}</span>
                    <span className="font-serif text-lg text-emerald-900">{r.count}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
